/**
 * Ciclo de vida da câmera do leitor, isolado do React para poder ser testado
 * com dublês. As duas coisas que dão errado em produção e passam batido em
 * teste manual moram aqui: escolher a câmera certa e desligá-la de verdade.
 */

export type GetUserMedia = (restricoes: MediaStreamConstraints) => Promise<MediaStream>;

export enum ErroCamera {
  PermissaoNegada = 'PERMISSAO_NEGADA',
  SemCamera = 'SEM_CAMERA',
  CameraOcupada = 'CAMERA_OCUPADA',
  ContextoInseguro = 'CONTEXTO_INSEGURO',
  DetectorIndisponivel = 'DETECTOR_INDISPONIVEL',
  Desconhecido = 'DESCONHECIDO',
}

export const MENSAGEM_ERRO_CAMERA: Record<ErroCamera, string> = {
  [ErroCamera.PermissaoNegada]:
    'Permissão de câmera negada. Libere o acesso nas configurações do navegador e tente de novo.',
  [ErroCamera.SemCamera]: 'Nenhuma câmera encontrada neste dispositivo.',
  [ErroCamera.CameraOcupada]:
    'A câmera está em uso por outro aplicativo. Feche o outro app e tente de novo.',
  [ErroCamera.ContextoInseguro]:
    'A câmera só funciona em HTTPS. Em teste local, acesse por localhost.',
  [ErroCamera.DetectorIndisponivel]:
    'Não foi possível carregar o leitor de códigos. Verifique a conexão.',
  [ErroCamera.Desconhecido]: 'Não foi possível abrir a câmera.',
};

/**
 * Pede a traseira, que é a que aponta para a peça. Quando o dispositivo não
 * tem como atender a restrição (notebook com webcam única), aceita qualquer
 * câmera em vez de falhar.
 */
export async function abrirCameraTraseira(getUserMedia: GetUserMedia): Promise<MediaStream> {
  try {
    return await getUserMedia({ video: { facingMode: 'environment' } });
  } catch (erro) {
    const classificado = classificarErroCamera(erro);

    // Permissão negada e ausência de câmera não melhoram afrouxando a
    // restrição — tentar de novo só atrasaria a mensagem de erro.
    if (
      classificado === ErroCamera.PermissaoNegada ||
      classificado === ErroCamera.SemCamera ||
      classificado === ErroCamera.CameraOcupada
    ) {
      throw erro;
    }

    return await getUserMedia({ video: true });
  }
}

/**
 * Desliga a câmera. Sem isso a luz fica acesa e o dispositivo trava para a
 * próxima abertura do leitor.
 */
export function pararStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) track.stop();
}

export function classificarErroCamera(erro: unknown): ErroCamera {
  const nome = (erro as { name?: unknown } | null)?.name;

  switch (nome) {
    case 'NotAllowedError':
    case 'SecurityError':
      return ErroCamera.PermissaoNegada;
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return ErroCamera.SemCamera;
    case 'NotReadableError':
    case 'TrackStartError':
      return ErroCamera.CameraOcupada;
    default:
      return ErroCamera.Desconhecido;
  }
}

/** `getUserMedia` só existe em contexto seguro (HTTPS ou localhost). */
export function cameraDisponivel(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}
